import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../context/AppContext";
import PageBackdrop from "../components/PageBackdrop";
import { Loader2, CheckCircle2, Package, Truck, Home as HomeIcon, Clock, MapPin, ExternalLink } from "lucide-react";

const STEPS = [
  { key: "paid",              label: "Order Placed",  icon: CheckCircle2 },
  { key: "packed",            label: "Packed",        icon: Package },
  { key: "shipped",           label: "In Transit",    icon: Truck },
  { key: "delivered",         label: "Delivered",     icon: HomeIcon },
];

function statusIndex(status) {
  if (status === "awaiting_payment") return -1;
  if (status === "paid") return 0;
  if (status === "packed") return 1;
  if (status === "shipped") return 2;
  if (status === "delivered") return 3;
  return 0;
}

function formatDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Track() {
  const { id: sessionIdParam } = useParams();
  const [sessionId, setSessionId] = useState(sessionIdParam || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOrder = useCallback(async (sid, mail) => {
    if (!sid) return;
    setLoading(true); setError(null);
    try {
      const url = mail ? `${API}/orders/track/${sid}?email=${encodeURIComponent(mail)}` : `${API}/orders/track/${sid}`;
      const r = await axios.get(url);
      setOrder(r.data);
    } catch (err) {
      setOrder(null);
      setError(err?.response?.status === 404 ? "We couldn't find an order with that reference." : "Something went wrong. Try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (sessionIdParam) fetchOrder(sessionIdParam);
  }, [sessionIdParam, fetchOrder]);

  const submit = (e) => {
    e.preventDefault();
    fetchOrder(sessionId.trim(), email.trim());
  };

  const idx = order ? statusIndex(order.status) : -1;

  return (
    <div className="bl-page min-h-screen bg-black pt-32 pb-24 px-6">
      <PageBackdrop image="/assets/bg/track.webp" dim={0.55} />
      <div className="bl-page-content max-w-4xl mx-auto">
        <div className="bl-eyebrow text-[color:var(--bl-gold)]">Order Status</div>
        <h1 className="bl-heading text-white text-5xl mt-3">Track</h1>
        <p className="text-white/50 text-sm mt-3 max-w-xl leading-relaxed">
          Enter your order reference (from your confirmation) plus the email you checked out with to see fulfilment status and tracking.
        </p>

        <form onSubmit={submit} className="mt-8 grid md:grid-cols-[2fr,1.5fr,auto] gap-3">
          <input
            value={sessionId}
            onChange={e => setSessionId(e.target.value)}
            placeholder="Order reference (session id)"
            required
            data-testid="track-sid"
            className="bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-4 py-3 text-white font-mono text-xs"
          />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email used at checkout (optional)"
            data-testid="track-email"
            className="bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-4 py-3 text-white font-mono text-xs"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="track-submit"
            className="bl-btn primary text-[11px] px-6 inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 size={13} className="animate-spin" /> : "Look Up"}
          </button>
        </form>

        {error && <div className="mt-8 border border-red-400/30 bg-red-400/5 text-red-200 p-4 text-sm" data-testid="track-error">{error}</div>}

        {order && (
          <div className="mt-10 space-y-8" data-testid="track-result">
            <div className="border border-white/8 p-6">
              <div className="flex flex-wrap justify-between items-baseline gap-3">
                <div>
                  <div className="bl-eyebrow text-white/40">Reference</div>
                  <div className="font-mono text-white text-sm mt-1 truncate max-w-xs md:max-w-md">{order.session_id}</div>
                </div>
                <div className="text-right">
                  <div className="bl-eyebrow text-white/40">Placed</div>
                  <div className="font-mono text-white/80 text-xs mt-1">{formatDT(order.created_at)}</div>
                </div>
              </div>

              {order.status === "awaiting_payment" ? (
                <div className="mt-6 border border-[color:var(--bl-gold)]/30 bg-[color:var(--bl-gold)]/5 p-4 text-[color:var(--bl-gold)] font-mono text-xs tracking-[0.15em] uppercase">
                  <Clock size={13} className="inline mr-2" /> Awaiting payment confirmation
                </div>
              ) : (
                <div className="mt-8">
                  <div className="grid grid-cols-4 gap-3">
                    {STEPS.map((s, i) => {
                      const Icon = s.icon;
                      const done = i <= idx;
                      const active = i === idx;
                      return (
                        <div key={s.key} data-testid={`track-step-${s.key}`} className="flex flex-col items-center text-center">
                          <div className={`w-11 h-11 rounded-full grid place-items-center border transition-all ${
                            done ? "border-[color:var(--bl-blue)] bg-[color:var(--bl-blue)]/10 text-[color:var(--bl-blue)]" : "border-white/15 text-white/30"
                          } ${active ? "animate-pulse" : ""}`}>
                            <Icon size={16} />
                          </div>
                          <div className={`mt-2 font-mono text-[10px] tracking-[0.2em] uppercase ${done ? "text-white" : "text-white/40"}`}>{s.label}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 h-1 bg-white/6 relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-[color:var(--bl-blue)] to-[color:var(--bl-gold)] transition-all duration-700"
                      style={{ width: `${Math.max(0, (idx / 3) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {order.tracking_number && (
                <div className="mt-6 flex flex-wrap items-center gap-3 p-4 border border-[color:var(--bl-blue)]/25 bg-[color:var(--bl-blue)]/[0.04]" data-testid="track-tracking-block">
                  <Truck size={16} className="text-[color:var(--bl-blue)]" />
                  <div className="min-w-0 flex-1">
                    <div className="bl-eyebrow text-[color:var(--bl-blue)]">Tracking · {order.carrier_label || "Courier"}</div>
                    <div className="font-mono text-white text-sm mt-1 truncate">{order.tracking_number}</div>
                  </div>
                  {order.tracking_url && (
                    <a href={order.tracking_url} target="_blank" rel="noreferrer"
                      data-testid="track-carrier-link"
                      className="bl-btn gold text-[10px] py-2 px-3 inline-flex items-center gap-2">
                      Track on {order.carrier_label} <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-white/8 p-6">
                <div className="bl-eyebrow text-[color:var(--bl-gold)] mb-4">Items</div>
                <div className="space-y-2">
                  {order.items.map((i, idxItem) => (
                    <div key={`${i.name}-${i.strength}-${idxItem}`} className="flex justify-between items-baseline text-sm">
                      <div className="text-white">{i.name} <span className="text-white/50">· {i.strength}</span></div>
                      <div className="font-mono text-xs text-white/60">×{i.quantity}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/8 flex justify-between items-baseline">
                  <div className="bl-eyebrow text-white/40">Total</div>
                  <div className="font-display text-white text-xl">A${(order.amount || 0).toFixed(2)}</div>
                </div>
              </div>

              <div className="border border-white/8 p-6">
                <div className="bl-eyebrow text-[color:var(--bl-gold)] mb-4">Delivering to</div>
                <div className="flex items-start gap-2 text-white/80 text-sm">
                  <MapPin size={14} className="mt-0.5 text-white/40 shrink-0" />
                  <div className="font-mono text-xs leading-relaxed">
                    {order.email_verified && order.shipping ? (
                      <>
                        {order.shipping.full_name}<br/>
                        {order.shipping.line1}{order.shipping.line2 ? `, ${order.shipping.line2}` : ""}<br/>
                        {order.ship_to_city}, {order.ship_to_state} {order.shipping.postcode}<br/>
                        {order.ship_to_country}
                      </>
                    ) : (
                      <>
                        {order.ship_to_city}, {order.ship_to_state}<br/>
                        {order.ship_to_country}
                        <div className="mt-2 text-white/40 text-[10px] tracking-[0.15em] uppercase">
                          Enter your email above to see the full address
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
              <TimelineCell label="Paid"      value={formatDT(order.paid_at)}      done={!!order.paid_at} />
              <TimelineCell label="Packed"    value={formatDT(order.fulfilled_at)} done={!!order.fulfilled_at} />
              <TimelineCell label="Shipped"   value={formatDT(order.shipped_at)}   done={!!order.shipped_at} />
              <TimelineCell label="Delivered" value="—" done={false} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineCell({ label, value, done }) {
  return (
    <div className="border border-white/8 p-4">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">{label}</div>
      <div className={`font-mono text-xs mt-2 ${done ? "text-white" : "text-white/30"}`}>{value}</div>
    </div>
  );
}
